import * as z from 'zod';
import { IMAGE_FORMATS } from './constants';

const imageFormatIds = IMAGE_FORMATS.map(({ id }) => id) as [string, ...string[]];
const imageFormats = z.enum(imageFormatIds);
const colors = z
  .string()
  .min(4)
  .max(9)
  .regex(/^#[0-9a-fA-F]+$/);
const sizes = z.enum(['none', 'small', 'medium', 'large']);
const modes = z.enum(['table', 'grid', 'events']);
const gridMode = z.enum(['grid', 'masonry']);
const gridItemImageMode = z.enum(['thumbnail', 'gallery']);
const sortDirection = z.enum(['asc', 'desc']);
const fontFamilies = z.string();
const textAlignments = z.enum(['left', 'center', 'right']);
const columnSettings = z.record(
  z.string(),
  z.object({
    hidden: z.boolean().optional(),
    showLabel: z.boolean().optional(),
    labelMode: z.enum(['above', 'inline']).optional(),
    filesDisplayMode: z.enum(['thumbs', 'list']).optional(),
    dateFormat: z.string().optional(),
    timeFormat: z.string().optional(),
    summarizeNumbers: z.boolean().optional(),
    enforceLineBreaks: z.boolean().optional(),
    excludeFromSearch: z.boolean().optional(),
    showAsFilter: z.boolean().optional(),
  }),
);

const boardFilters = z
  .object({
    rules: z
      .array(
        z.object({
          column_id: z.string(),
          operator: z.string(),
          compare_attribute_id: z.string().optional(),
          compare_value: z.array(z.object({ id: z.string(), value: z.any() })),
        }),
      )
      .optional(),
    order_by_column_id: z.string().optional(),
    order_by_direction: z.enum(['asc', 'desc']).optional(),
    operator: z.enum(['and', 'or']).optional(),
  })
  .optional();

export const settingsSchema = z.object({
  // the widget's display name, used to tell several widgets on the same board apart.
  // it must stay declared here: this schema validates the whole `data` blob on PUT /settings and
  // zod strips undeclared keys, so an unlisted field would be dropped on every save.
  name: z.string().max(100).optional(),

  mode: modes,
  bodyPadding: sizes,
  primaryColor: colors,
  secondaryColor: colors,
  borderColor: colors,
  backgroundColor: colors,
  headline: z.string().optional(),

  fontFamily: fontFamilies,
  fontSize: z.number(),
  textAlignment: textAlignments,

  sortColumn: z.string().nullable(),
  sortDirection: sortDirection.nullable(),

  // shared settings
  search: z.boolean(),

  // table settings
  tableShowHeader: z.boolean(),
  tableCellPadding: sizes,
  tableBorderRadius: sizes,
  tableDefaultItemsPerPage: z.number(),

  // grid settings
  gridMode: gridMode.nullable(),
  gridItemsPerPage: z.number().optional(),
  gridItemPadding: sizes,
  gridItemGap: sizes,
  gridItemBorderRadius: sizes,
  gridItemImageColumn: z.string().nullable(),
  gridItemImageColumnFormat: imageFormats.nullable(),
  gridItemImageMode: gridItemImageMode.optional(),
  gridItemHeadlineColumn: z.string().nullable(),

  // events settings
  eventsItemPadding: sizes,
  eventsItemGap: sizes,
  eventsItemBorderRadius: sizes,
  eventsItemImageColumn: z.string().nullable(),
  eventsItemHeadlineColumn: z.string().nullable(),
  eventsItemDateColumn: z.string().nullable(),
  eventsItemLocationColumn: z.string().nullable(),
  eventsItemDescriptionColumn: z.string().nullable(),
  eventsItemLearnMoreColumn: z.string().nullable(),
  eventsDateFormat: z.string().nullable(),
  eventsTimeFormat: z.string().nullable(),

  // advanced settings
  columnSettings: columnSettings,

  // board filters
  boardFilters: boardFilters,

  // custom labels
  customLabels: z.record(z.string(), z.string()).optional(),

  enableSubitemsSupport: z.boolean().optional(),

  enableFormulaSupport: z.boolean().optional(),
});

export type Settings = z.infer<typeof settingsSchema>;
export type ColumnSettings = z.infer<typeof columnSettings>;
